import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Popover, Segmented, Tooltip } from "antd";
import { AudioOutlined, DesktopOutlined, EyeOutlined, MobileOutlined, MessageOutlined, PlayCircleOutlined, StopOutlined } from "@ant-design/icons";
import { urlMiniatura } from "../inicio/MisExperiencias";

const URL_VISTA = `${process.env.PUBLIC_URL || ""}/asistente/vista-previa.html`;
const API_ASISTENTE = `${process.env.REACT_APP_BASE_URL}/asistente`;

//la vista previa se dibuja a tamaño real y se achica con CSS: asi el componente decide igual que en
//la experiencia (en un iframe de 600px se creeria un celular y cambiaria tamaños y controles)
const PANTALLAS = {
  computadora: { ancho: 1280, alto: 720 },
  celular: { ancho: 390, alto: 780 },
};
const ALTO_MAX_CELULAR = 520;

/**
 * Muestra el asistente como lo va a ver el visitante, sobre la escena principal de la experiencia,
 * con lo que se está editando (sin guardar). La guía es la imagen de muestra del avatar recortada
 * igual que el video real; si el proveedor no tiene muestra se ve una maqueta para acomodarlo igual.
 * "Probar en vivo" abre el avatar de verdad: solo la imagen (sin sonido ni micrófono) o una charla.
 */
export const VistaPreviaAsistente = ({ experiencia, config, avatarId, puedeProbar, minutosPorSesion }) => {
  const marcoRef = useRef(null);
  const iframeRef = useRef(null);
  const [lista, setLista] = useState(false);
  const [pantalla, setPantalla] = useState("computadora");
  const [escala, setEscala] = useState(0.5);
  const [muestra, setMuestra] = useState({ estado: "cargando" });
  const [enVivo, setEnVivo] = useState(false);
  const [conImagen, setConImagen] = useState(false);
  //desde "Probar en vivo" hasta "Terminar prueba" la vista previa acepta clics: aunque la charla no
  //llegue a abrirse (sin lugar, sin permiso de micrófono) hay que poder usar las tarjetas que muestra
  const [probando, setProbando] = useState(false);
  //"silencio": solo la imagen del avatar · "charla": con micrófono y sonido
  const [modoPrueba, setModoPrueba] = useState(null);
  const [eligiendo, setEligiendo] = useState(false);

  const enviar = useCallback((datos) => {
    const ventana = iframeRef.current && iframeRef.current.contentWindow;
    if (ventana) ventana.postMessage(datos, window.location.origin);
  }, []);

  useEffect(() => {
    const alRecibir = (e) => {
      if (e.origin !== window.location.origin || e.source !== iframeRef.current?.contentWindow) return;
      const m = e.data || {};
      if (m.tipo === "lista") setLista(true);
      if (m.tipo === "muestra")
        setMuestra(m.ok ? { estado: "lista" } : { estado: m.maqueta ? "maqueta" : "error", mensaje: m.mensaje });
      if (m.tipo === "sesion") {
        setEnVivo(Boolean(m.abierta));
        if (!m.abierta) setConImagen(false);
        //terminar la charla (Terminar, despedida del agente, tope de tiempo) también termina la prueba
        if (m.terminada) {
          enviar({ tipo: "salir" });
          setProbando(false);
          setModoPrueba(null);
        }
      }
      if (m.tipo === "avatar") setConImagen(true);
    };
    window.addEventListener("message", alRecibir);
    return () => window.removeEventListener("message", alRecibir);
  }, [enviar]);

  //al cambiar de experiencia o de pantalla el iframe se recarga y avisa de nuevo cuando esta listo
  useEffect(() => {
    setLista(false);
    setEnVivo(false);
    setProbando(false);
    setModoPrueba(null);
  }, [experiencia.id, pantalla]);

  const probar = (modo) => {
    setEligiendo(false);
    setProbando(true);
    setModoPrueba(modo);
    enviar({ tipo: "vivo", silencio: modo === "silencio" });
  };

  const terminarPrueba = () => {
    enviar({ tipo: "salir" });
    setProbando(false);
    setModoPrueba(null);
  };

  const { ancho, alto } = PANTALLAS[pantalla];
  useEffect(() => {
    const marco = marcoRef.current;
    if (!marco) return undefined;
    const medir = () => {
      const disponible = marco.clientWidth || ancho;
      setEscala(pantalla === "celular" ? Math.min(disponible / ancho, ALTO_MAX_CELULAR / alto) : disponible / ancho);
    };
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(marco);
    return () => obs.disconnect();
  }, [pantalla, ancho, alto]);

  useEffect(() => {
    if (!lista) return;
    enviar({ tipo: "config", config: { api: API_ASISTENTE, experienciaId: experiencia.id, avatarId, ...config } });
  }, [lista, config, avatarId, experiencia.id, enviar]);

  useEffect(() => {
    if (!lista) return;
    enviar({ tipo: "fondo", url: urlMiniatura(experiencia.portadaEscenaId) });
    enviar({ tipo: "invitar" });
  }, [lista, experiencia.portadaEscenaId, enviar]);

  useEffect(() => {
    if (!lista) return undefined;
    setMuestra({ estado: "cargando" });
    //espera a que se termine de escribir el ID del avatar antes de pedir la imagen
    const t = setTimeout(() => {
      const consulta = avatarId ? `?avatarId=${encodeURIComponent(avatarId)}` : "";
      enviar({ tipo: "muestra", url: `${API_ASISTENTE}/vista-previa/${encodeURIComponent(experiencia.id)}${consulta}` });
    }, 500);
    return () => clearTimeout(t);
  }, [lista, avatarId, experiencia.id, enviar]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Segmented
          value={pantalla}
          onChange={setPantalla}
          disabled={probando}
          options={[
            { value: "computadora", label: "Computadora", icon: <DesktopOutlined /> },
            { value: "celular", label: "Celular", icon: <MobileOutlined /> },
          ]}
        />
        <span style={{ flexGrow: 1 }} />
        <Button icon={<MessageOutlined />} disabled={probando} onClick={() => enviar({ tipo: "invitar" })}>
          Ver la invitación
        </Button>
        {probando ? (
          <Button danger icon={<StopOutlined />} onClick={terminarPrueba}>
            Terminar prueba
          </Button>
        ) : (
          <Tooltip title={puedeProbar ? "" : "Activá y guardá el asistente para probarlo en vivo"}>
            <Popover
              trigger="click"
              placement="bottomRight"
              open={eligiendo && puedeProbar}
              onOpenChange={(v) => setEligiendo(v && puedeProbar)}
              title="Probar en vivo"
              content={
                <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 300 }}>
                  <span className="inm-tenue" style={{ fontSize: 12 }}>
                    Abre el avatar real. Gasta minutos del mes (hasta {minutosPorSesion} min por prueba).
                  </span>
                  <Button block type="primary" className="inm-btn-asistente" icon={<EyeOutlined />} onClick={() => probar("silencio")}>
                    Solo verlo, sin sonido
                  </Button>
                  <span className="inm-tenue" style={{ fontSize: 12, marginTop: -4 }}>
                    Para acomodarlo: no usa el micrófono y no se escucha nada.
                  </span>
                  <Button block icon={<AudioOutlined />} onClick={() => probar("charla")}>
                    Charlar con el avatar
                  </Button>
                </div>
              }
            >
              <Button type="primary" className="inm-btn-asistente" icon={<PlayCircleOutlined />} disabled={!puedeProbar}>
                Probar en vivo
              </Button>
            </Popover>
          </Tooltip>
        )}
      </div>

      <div ref={marcoRef} style={{ width: "100%" }}>
        <div
          style={{
            width: ancho * escala,
            height: alto * escala,
            margin: "0 auto",
            overflow: "hidden",
            borderRadius: pantalla === "celular" ? 18 : 12,
            border: "1px solid var(--inm-borde)",
            background: "var(--inm-superficie)",
          }}
        >
          <iframe
            key={`${experiencia.id}-${pantalla}`}
            ref={iframeRef}
            title="Vista previa del asistente"
            src={URL_VISTA}
            allow="microphone; autoplay"
            style={{
              width: ancho,
              height: alto,
              border: 0,
              transform: `scale(${escala})`,
              transformOrigin: "0 0",
              //fuera de la prueba en vivo es solo para mirar: que un clic no abra una charla sin aviso
              pointerEvents: probando ? "auto" : "none",
            }}
          />
        </div>
      </div>

      <span className="inm-tenue" style={{ fontSize: 12 }}>
        {probando && modoPrueba === "silencio"
          ? conImagen
            ? "Avatar en vivo, sin sonido ni micrófono. Movelo con los controles; tocá Terminar prueba cuando termines."
            : "Abriendo el avatar en vivo, sin sonido…"
          : enVivo
          ? "Charla en vivo: podés hablarle o escribirle desde la vista previa."
          : probando
          ? "Prueba en vivo: usá la vista previa como un visitante. Tocá Terminar prueba para volver."
          : muestra.estado === "cargando"
          ? "Cargando la imagen del avatar… mientras tanto ves la maqueta."
          : muestra.estado === "maqueta" || muestra.estado === "error"
          ? `Ves una maqueta del avatar para acomodarlo: no pudimos traer su imagen de muestra (${muestra.mensaje || "sin detalle"}). Para ver el avatar real usá Probar en vivo › Solo verlo.`
          : "Vista previa con la imagen de muestra del avatar. Los cambios se ven al instante, sin guardar."}
      </span>
    </div>
  );
};

export default VistaPreviaAsistente;
